// Per-viewer memory (a collapsed group, a remembered tab) in localStorage, inside try/catch because storage can be
// unavailable. Under capture nothing is read or written: every such state starts at its default (ground rule 8).

import { useCallback, useState } from "react";
import { useMc } from "./McRoot";

const PREFIX = "ta.mc.";

function store(): Storage | null {
  try {
    return (globalThis as { localStorage?: Storage }).localStorage ?? null;
  } catch {
    return null;
  }
}

/** The stored value, or the fallback (always the fallback under capture). */
export function readViewerMemory<T>(key: string, fallback: T, capture: boolean): T {
  if (capture) return fallback;
  try {
    const raw = store()?.getItem(PREFIX + key);
    return raw === null || raw === undefined ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

/** Stores the value; never under capture. */
export function writeViewerMemory<T>(key: string, value: T, capture: boolean): void {
  if (capture) return;
  try {
    store()?.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // Keep the in-memory value only.
  }
}

/** useState remembered per viewer under `key`, skipped entirely under capture; a null key is plain useState. */
export function useViewerMemory<T>(key: string | null, fallback: T): [T, (next: T) => void] {
  const { capture } = useMc();
  const [value, setValue] = useState<T>(() => (key === null || typeof window === "undefined" ? fallback : readViewerMemory(key, fallback, capture)));
  const set = useCallback((next: T) => {
    setValue(next);
    if (key !== null) writeViewerMemory(key, next, capture);
  }, [key, capture]);
  return [value, set];
}
